// src/services/SearchService.js
import { t } from '../useTranslations';
import bwraService from './BWRAService';
import geoRiskService from './GeoRiskService';
import customerRiskService from './CustomerRiskService';
import transactionMonitoringService from './TransactionMonitoringService';
import periodicReportingService from './PeriodicReportingService';
import userService from './UserService';

// Minimum query length to trigger search
const MIN_QUERY_LENGTH = 2;

// Maximum results per module
const MAX_PER_MODULE = 5;

function matchesQuery(text, query) {
  if (!text || !query) return false;
  return String(text).toLowerCase().includes(query.toLowerCase());
}

function scoreMatch(text, query) {
  if (!text || !query) return 0;
  const t = String(text).toLowerCase();
  const q = query.toLowerCase();
  if (t === q) return 3;
  if (t.startsWith(q)) return 2;
  return 1;
}

class SearchService {
  /**
   * Search all modules for items matching the query string.
   * Returns an array of result groups, one per module that has matches.
   * Each group: { moduleId, moduleKey, route, results: [{ id, title, subtitle, route }] }
   */
  search(query) {
    if (!query || query.trim().length < MIN_QUERY_LENGTH) return [];

    const q = query.trim();
    const groups = [];

    // BWRA — risks
    try {
      const risks = bwraService.getRisks();
      const matches = risks
        .filter(r =>
          matchesQuery(r.name, q) ||
          matchesQuery(r.category, q) ||
          matchesQuery(r.description, q)
        )
        .map(r => ({
          id: r.id,
          title: r.name,
          subtitle: r.category || '',
          route: `/bwra?tab=register&id=${encodeURIComponent(r.id)}`,
          score: scoreMatch(r.name, q) + scoreMatch(r.category, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (matches.length > 0) {
        groups.push({
          moduleId: 'bwra',
          moduleKey: 'common.modules.BWRA',
          route: '/bwra',
          results: matches,
        });
      }
    } catch (e) {
      // service unavailable — skip
    }

    // BWRA — controls (nested under risks)
    try {
      const risks = bwraService.getRisks();
      const controlMatches = risks.flatMap(risk =>
        (risk.controls || [])
          .filter(c =>
            matchesQuery(c.name, q) ||
            matchesQuery(c.description, q) ||
            matchesQuery(c.type, q)
          )
          .map(c => ({
            id: `ctrl-${c.id}`,
            title: c.name,
            subtitle: risk.name,
            route: `/bwra?tab=register&id=${encodeURIComponent(risk.id)}`,
            score: scoreMatch(c.name, q),
          }))
      )
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (controlMatches.length > 0) {
        const existing = groups.find(g => g.moduleId === 'bwra');
        if (existing) {
          const combined = [...existing.results, ...controlMatches];
          const seen = new Set();
          existing.results = combined.filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          }).slice(0, MAX_PER_MODULE);
        } else {
          groups.push({
            moduleId: 'bwra',
            moduleKey: 'common.modules.BWRA',
            route: '/bwra',
            results: controlMatches,
          });
        }
      }
    } catch (e) {
      // skip
    }

    // BWRA — actions
    try {
      const actions = bwraService.getAllActions();
      const actionMatches = actions
        .filter(a =>
          matchesQuery(a.description, q) ||
          matchesQuery(a.responsible, q) ||
          matchesQuery(a.riskName, q)
        )
        .map(a => ({
          id: `act-${a.id}`,
          title: a.description ? a.description.substring(0, 60) : a.id,
          subtitle: a.riskName || '',
          route: `/bwra?tab=register&id=${encodeURIComponent(a.riskId)}`,
          score: scoreMatch(a.description, q) + scoreMatch(a.riskName, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (actionMatches.length > 0) {
        const existing = groups.find(g => g.moduleId === 'bwra');
        if (existing) {
          const combined = [...existing.results, ...actionMatches];
          const seen = new Set();
          existing.results = combined.filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          }).slice(0, MAX_PER_MODULE);
        } else {
          groups.push({
            moduleId: 'bwra',
            moduleKey: 'common.modules.BWRA',
            route: '/bwra',
            results: actionMatches,
          });
        }
      }
    } catch (e) {
      // skip
    }

    // GeoRisk — indexes
    try {
      const indexes = geoRiskService.getIndexes();
      const matches = indexes
        .filter(idx =>
          matchesQuery(idx.name, q) ||
          matchesQuery(idx.description, q) ||
          matchesQuery(idx.source, q)
        )
        .map(idx => ({
          id: idx.id,
          title: idx.name,
          subtitle: idx.source || '',
          route: `/geographical-risk?tab=risk-management&index=${encodeURIComponent(idx.id)}`,
          score: scoreMatch(idx.name, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (matches.length > 0) {
        groups.push({
          moduleId: 'geographical-risk',
          moduleKey: 'common.modules.GeoRisk',
          route: '/geographical-risk',
          results: matches,
        });
      }
    } catch (e) {
      // skip
    }

    // GeoRisk — countries
    try {
      const countries = geoRiskService.getCountries ? geoRiskService.getCountries() : [];
      const matches = countries
        .filter(c =>
          matchesQuery(c.name, q) ||
          matchesQuery(c.code, q)
        )
        .map(c => ({
          id: c.code || c.name,
          title: c.name,
          subtitle: c.code || '',
          route: `/geographical-risk?tab=countries&country=${encodeURIComponent(c.code || c.name)}`,
          score: scoreMatch(c.name, q) + scoreMatch(c.code, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (matches.length > 0) {
        const existing = groups.find(g => g.moduleId === 'geographical-risk');
        if (existing) {
          // merge, dedup by id, keep top MAX_PER_MODULE
          const combined = [...existing.results, ...matches];
          const seen = new Set();
          existing.results = combined.filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          }).slice(0, MAX_PER_MODULE);
        } else {
          groups.push({
            moduleId: 'geographical-risk',
            moduleKey: 'common.modules.GeoRisk',
            route: '/geographical-risk',
            results: matches,
          });
        }
      }
    } catch (e) {
      // skip
    }

    // CustomerRisk — models
    try {
      const models = customerRiskService.getModels();
      const matches = models
        .filter(m =>
          matchesQuery(m.name, q) ||
          matchesQuery(m.description, q)
        )
        .map(m => ({
          id: m.name,
          title: m.name,
          subtitle: m.version ? `v${m.version.major}.${m.version.minor}` : '',
          route: `/customer-risk?tab=model-builder&model=${encodeURIComponent(m.name)}`,
          score: scoreMatch(m.name, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (matches.length > 0) {
        groups.push({
          moduleId: 'customer-risk',
          moduleKey: 'common.modules.CustomerRisk',
          route: '/customer-risk',
          results: matches,
        });
      }
    } catch (e) {
      // skip
    }

    // CustomerRisk — parameters
    try {
      const parameters = customerRiskService.getParameters ? customerRiskService.getParameters() : [];
      const matches = parameters
        .filter(p =>
          matchesQuery(p.name, q) ||
          matchesQuery(p.label, q) ||
          matchesQuery(p.description, q)
        )
        .map(p => ({
          id: p.id || p.name,
          title: p.label || p.name,
          subtitle: p.name || '',
          route: `/customer-risk?tab=parameters&param=${encodeURIComponent(String(p.id || p.name))}`,
          score: scoreMatch(p.label || p.name, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (matches.length > 0) {
        const existing = groups.find(g => g.moduleId === 'customer-risk');
        if (existing) {
          const combined = [...existing.results, ...matches];
          const seen = new Set();
          existing.results = combined.filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          }).slice(0, MAX_PER_MODULE);
        } else {
          groups.push({
            moduleId: 'customer-risk',
            moduleKey: 'common.modules.CustomerRisk',
            route: '/customer-risk',
            results: matches,
          });
        }
      }
    } catch (e) {
      // skip
    }

    // TransactionMonitoring — rules
    try {
      const rules = transactionMonitoringService.getRules();
      const matches = rules
        .filter(r =>
          matchesQuery(r.name, q) ||
          matchesQuery(r.description, q) ||
          matchesQuery(r.category, q)
        )
        .map(r => ({
          id: r.id,
          title: r.name,
          subtitle: r.category || '',
          route: `/transaction-monitoring?tab=rules&rule=${encodeURIComponent(r.id)}`,
          score: scoreMatch(r.name, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (matches.length > 0) {
        groups.push({
          moduleId: 'transaction-monitoring',
          moduleKey: 'common.modules.TransactionMonitoring',
          route: '/transaction-monitoring',
          results: matches,
        });
      }
    } catch (e) {
      // skip
    }

    // PeriodicReporting — reports
    try {
      const reports = periodicReportingService.getReports();
      const matches = reports
        .filter(r =>
          matchesQuery(String(r.year), q) ||
          matchesQuery(r.status, q) ||
          matchesQuery(r.name, q)
        )
        .map(r => ({
          id: r.id,
          title: r.name || String(r.year),
          subtitle: r.status || '',
          route: `/periodic-reporting?report=${encodeURIComponent(r.id)}`,
          score: scoreMatch(String(r.year), q) + scoreMatch(r.name, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (matches.length > 0) {
        groups.push({
          moduleId: 'periodic-reporting',
          moduleKey: 'common.modules.PeriodicReporting',
          route: '/periodic-reporting',
          results: matches,
        });
      }
    } catch (e) {
      // skip
    }

    // Admin — users
    try {
      const users = userService.getUsers();
      const userMatches = users
        .filter(u =>
          matchesQuery(u.firstName, q) ||
          matchesQuery(u.lastName, q) ||
          matchesQuery(u.email, q) ||
          matchesQuery(`${u.firstName} ${u.lastName}`, q)
        )
        .map(u => ({
          id: `usr-${u.id}`,
          title: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
          subtitle: u.email || '',
          route: `/admin?tab=users`,
          score: scoreMatch(`${u.firstName} ${u.lastName}`, q) + scoreMatch(u.email, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (userMatches.length > 0) {
        groups.push({
          moduleId: 'admin',
          moduleKey: 'common.modules.Admin',
          route: '/admin',
          results: userMatches,
        });
      }
    } catch (e) {
      // skip
    }

    // Admin — roles
    try {
      const roles = userService.getRoles();
      const roleMatches = roles
        .filter(r =>
          matchesQuery(r.label, q) ||
          matchesQuery(r.id, q)
        )
        .map(r => ({
          id: `role-${r.id}`,
          title: r.label || r.id,
          subtitle: r.isBuiltIn ? t('modules.administration.users.builtIn') : t('modules.administration.users.customRoleLabel'),
          route: `/admin?tab=users`,
          score: scoreMatch(r.label, q) + scoreMatch(r.id, q),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PER_MODULE);

      if (roleMatches.length > 0) {
        const existing = groups.find(g => g.moduleId === 'admin');
        if (existing) {
          const combined = [...existing.results, ...roleMatches];
          const seen = new Set();
          existing.results = combined.filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          }).slice(0, MAX_PER_MODULE);
        } else {
          groups.push({
            moduleId: 'admin',
            moduleKey: 'common.modules.Admin',
            route: '/admin',
            results: roleMatches,
          });
        }
      }
    } catch (e) {
      // skip
    }

    return groups;
  }
}

const searchService = new SearchService();
export default searchService;
